import React, { useEffect, useState } from "react";
import { apiFetchVeyaTeam } from "../../lib/api";


const emptyForm = {
  title: "",
  subtitle: "",
  images: "",
  notes: "",
  is_live: false,
};

function makeSlug(title) {
  return String(title || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-");
}

function parseLines(value) {
  return String(value || "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
}

function resolveImageUrl(url) {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;

  if (url.startsWith("/tryon-assets/")) {
    return `${process.env.REACT_APP_API_BASE_URL || "http://localhost:8000"}${url}`;
  }

  return url;
}

function moveItem(list, fromIndex, toIndex) {
  const next = [...list];
  const [moved] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, moved);
  return next;
}

export default function VeyaTeamEdits() {
  const [edits, setEdits] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [draggedEditIndex, setDraggedEditIndex] = useState(null);
  const [dragOverEditIndex, setDragOverEditIndex] = useState(null);

  async function loadEdits() {
    setLoading(true);
    setError("");

    try {
      const data = await apiFetchVeyaTeam("/veya-team/edits/");
      setEdits(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to load Veya edits", err);
      setError(err?.message || "Could not load edits.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadEdits();
  }, []);

  function updateField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function closeCreateModal() {
    if (submitting || uploading) return;
    setShowCreateModal(false);
    setForm(emptyForm);
  }

  async function handleUpload(files) {
    if (!files || files.length === 0) return;

    setUploading(true);
    setError("");

    try {
      const uploadedUrls = [];

      for (const file of files) {
        const formData = new FormData();
        formData.append("file", file);

        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/veya-team/edits/upload-image`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("veya_team_token")}`,
          },
          body: formData,
        });

        const data = await res.json();

        if (!res.ok) {
          throw new Error(data?.detail || "Image upload failed");
        }

        if (data?.url) {
          uploadedUrls.push(data.url);
        }
      }

      setForm((prev) => ({
        ...prev,
        images: [...parseLines(prev.images), ...uploadedUrls].join("\n"),
      }));
    } catch (err) {
      console.error("Upload failed", err);
      setError(err?.message || "Image upload failed.");
    } finally {
      setUploading(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();

    const title = form.title.trim();
    const subtitle = form.subtitle.trim();
    const slug = makeSlug(title);

    if (!title) {
      setError("Title is required.");
      return;
    }

    if (!slug) {
      setError("Could not create a valid slug from that title.");
      return;
    }

    setSubmitting(true);
    setError("");

    try {
      await apiFetchVeyaTeam("/veya-team/edits/", {
        method: "POST",
        body: JSON.stringify({
          slug,
          title,
          subtitle: subtitle || null,
          image_dir: null,
          images: parseLines(form.images),
          notes: parseLines(form.notes),
          is_live: form.is_live,
        }),
      });

      setForm(emptyForm);
      setShowCreateModal(false);
      await loadEdits();
    } catch (err) {
      console.error("Failed to create Veya edit", err);
      setError(err?.message || "Could not create edit.");
    } finally {
      setSubmitting(false);
    }
  }

  async function toggleLive(edit) {
    try {
      await apiFetchVeyaTeam(`/veya-team/edits/${edit.id}/live`, {
        method: "PATCH",
        body: JSON.stringify({ is_live: !edit.is_live }),
      });

      await loadEdits();
    } catch (err) {
      console.error("Failed to update live status", err);
      setError(err?.message || "Could not update live status.");
    }
  }

  async function persistEditOrder(nextEdits) {
    try {
      await apiFetchVeyaTeam("/veya-team/edits/reorder", {
        method: "PATCH",
        body: JSON.stringify({
          edit_ids: nextEdits.map((edit) => edit.id),
        }),
      });
    } catch (err) {
      console.error("Failed to reorder edits", err);
      setError(err?.message || "Could not reorder edits.");
      await loadEdits();
    }
  }

  async function handleEditDrop(toIndex) {
    if (draggedEditIndex === null || draggedEditIndex === toIndex) {
      setDraggedEditIndex(null);
      setDragOverEditIndex(null);
      return;
    }

    const nextEdits = moveItem(edits, draggedEditIndex, toIndex);

    setEdits(nextEdits);
    setDraggedEditIndex(null);
    setDragOverEditIndex(null);

    await persistEditOrder(nextEdits);
  }

  return (
    <div style={{ display: "grid", gap: 18, paddingBottom: 32 }}>
      <div
        className="merchant-card"
        style={{
          padding: 22,
          overflow: "visible",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 16,
          }}
        >
          <div>
            <h2 style={{ margin: 0, lineHeight: 1.15 }}>Editorial edits</h2>
            <p style={{ margin: "8px 0 0", opacity: 0.72 }}>
              Manage Veya’s public mood edits. Drag cards to reorder how they appear on the Moods page.
            </p>
          </div>

          <button
            className="gold-btn"
            type="button"
            onClick={() => {
              setError("");
              setShowCreateModal(true);
            }}
            style={{
              flexShrink: 0,
              minHeight: 44,
              padding: "10px 18px",
            }}
          >
            Create edit
          </button>
        </div>

        {loading ? <p style={{ marginTop: 18 }}>Loading edits...</p> : null}

        {error ? (
          <p className="merchant-error" style={{ marginTop: 14 }}>
            {error}
          </p>
        ) : null}

        {!loading && edits.length === 0 ? (
          <p style={{ marginTop: 18 }}>No Veya edits created yet.</p>
        ) : null}

        <div style={{ display: "grid", gap: 12, marginTop: 18 }}>
          {edits.map((edit, index) => {
            const images = Array.isArray(edit.images) ? edit.images : [];
            const notes = Array.isArray(edit.notes) ? edit.notes : [];
            const isDragging = draggedEditIndex === index;
            const isDragTarget = dragOverEditIndex === index && draggedEditIndex !== index;

            return (
              <div
                key={edit.id}
                draggable
                onDragStart={(e) => {
                  setDraggedEditIndex(index);
                  e.dataTransfer.effectAllowed = "move";
                  e.dataTransfer.setData("text/plain", String(edit.id));
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = "move";
                  setDragOverEditIndex(index);
                }}
                onDragLeave={() => {
                  setDragOverEditIndex(null);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  handleEditDrop(index);
                }}
                onDragEnd={() => {
                  setDraggedEditIndex(null);
                  setDragOverEditIndex(null);
                }}
                style={{
                  border: isDragTarget
                    ? "1px solid rgba(91,31,43,0.55)"
                    : "1px solid rgba(176,140,108,0.22)",
                  borderRadius: 18,
                  padding: 14,
                  background: isDragTarget
                    ? "rgba(91,31,43,0.045)"
                    : "rgba(251,247,242,0.55)",
                  opacity: isDragging ? 0.55 : 1,
                  cursor: "grab",
                  boxShadow: isDragTarget
                    ? "0 18px 40px rgba(91,31,43,0.10)"
                    : "none",
                }}
              >
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "auto minmax(0, 1fr) auto",
                    gap: 14,
                    alignItems: "start",
                  }}
                >
                  <div
                    title="Drag to reorder"
                    style={{
                      width: 34,
                      height: 34,
                      borderRadius: 12,
                      display: "grid",
                      placeItems: "center",
                      border: "1px solid rgba(91,31,43,0.16)",
                      background: "rgba(255,255,255,0.68)",
                      color: "#5b1f2b",
                      fontWeight: 900,
                      letterSpacing: -2,
                      userSelect: "none",
                    }}
                  >
                    ⋮⋮
                  </div>

                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        flexWrap: "wrap",
                      }}
                    >
                      <strong
                        style={{
                          fontSize: 17,
                          lineHeight: 1.25,
                          wordBreak: "break-word",
                        }}
                      >
                        {edit.title}
                      </strong>

                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 800,
                          letterSpacing: "0.08em",
                          textTransform: "uppercase",
                          color: edit.is_live ? "#2f6b3f" : "#7a5a2b",
                          background: edit.is_live
                            ? "rgba(47,107,63,0.10)"
                            : "rgba(122,90,43,0.10)",
                          border: edit.is_live
                            ? "1px solid rgba(47,107,63,0.16)"
                            : "1px solid rgba(122,90,43,0.16)",
                          borderRadius: 999,
                          padding: "4px 8px",
                        }}
                      >
                        {edit.is_live ? "LIVE" : "DRAFT"}
                      </span>
                    </div>

                    <div
                      style={{
                        fontSize: 13,
                        opacity: 0.7,
                        marginTop: 3,
                        wordBreak: "break-word",
                      }}
                    >
                      /{edit.slug}
                    </div>

                    {edit.subtitle ? (
                      <p
                        style={{
                          margin: "8px 0 0",
                          lineHeight: 1.35,
                          opacity: 0.86,
                        }}
                      >
                        {edit.subtitle}
                      </p>
                    ) : null}

                    {notes.length ? (
                      <div
                        style={{
                          display: "flex",
                          gap: 6,
                          flexWrap: "wrap",
                          marginTop: 10,
                        }}
                      >
                        {notes.slice(0, 6).map((note, noteIndex) => (
                          <span
                            key={`${note}_${noteIndex}`}
                            style={{
                              fontSize: 12,
                              padding: "4px 8px",
                              borderRadius: 999,
                              background: "rgba(91,31,43,0.08)",
                              border: "1px solid rgba(91,31,43,0.12)",
                            }}
                          >
                            {note}
                          </span>
                        ))}
                      </div>
                    ) : null}

                    <div style={{ marginTop: 12 }}>
                      <div style={{ fontSize: 13, opacity: 0.7, marginBottom: 8 }}>
                        Images: {images.length}
                      </div>

                      {images.length ? (
                        <div
                          style={{
                            display: "flex",
                            gap: 8,
                            flexWrap: "wrap",
                          }}
                        >
                          {images.slice(0, 10).map((imageUrl, imageIndex) => (
                            <div
                              key={`${imageUrl}_${imageIndex}`}
                              style={{
                                width: 54,
                                height: 68,
                                borderRadius: 12,
                                overflow: "hidden",
                                border: "1px solid rgba(176,140,108,0.26)",
                                background: "rgba(255,255,255,0.65)",
                              }}
                            >
                              <img
                                src={resolveImageUrl(imageUrl)}
                                alt={`${edit.title} thumbnail ${imageIndex + 1}`}
                                draggable={false}
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                  display: "block",
                                }}
                              />
                            </div>
                          ))}

                          {images.length > 10 ? (
                            <div
                              style={{
                                width: 54,
                                height: 68,
                                borderRadius: 12,
                                border: "1px solid rgba(176,140,108,0.26)",
                                background: "rgba(255,255,255,0.65)",
                                display: "grid",
                                placeItems: "center",
                                fontSize: 12,
                                fontWeight: 800,
                                color: "#5b1f2b",
                              }}
                            >
                              +{images.length - 10}
                            </div>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-end",
                      gap: 8,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 12,
                        opacity: 0.55,
                        whiteSpace: "nowrap",
                      }}
                    >
                      #{index + 1}
                    </div>

                    <button
                      type="button"
                      onClick={() => toggleLive(edit)}
                      style={{
                        fontSize: 12,
                        padding: "6px 10px",
                        borderRadius: 10,
                        border: "1px solid rgba(0,0,0,0.1)",
                        background: "white",
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {edit.is_live ? "Unpublish" : "Publish"}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {showCreateModal ? (
        <div
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeCreateModal();
          }}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1000,
            display: "grid",
            placeItems: "center",
            padding: 18,
            background: "rgba(35,24,24,0.28)",
            backdropFilter: "blur(7px)",
          }}
        >
          <div
            className="merchant-card"
            role="dialog"
            aria-modal="true"
            aria-label="Create Veya edit"
            style={{
              width: "min(760px, calc(100vw - 36px))",
              maxHeight: "calc(100vh - 44px)",
              overflow: "auto",
              padding: 24,
              boxShadow: "0 28px 80px rgba(35,24,24,0.22)",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: 16,
                marginBottom: 16,
              }}
            >
              <div>
                <h2 style={{ margin: 0, lineHeight: 1.15 }}>Create Veya edit</h2>
                <p style={{ margin: "8px 0 0", opacity: 0.72 }}>
                  Add a title, styling notes, and upload mood images.
                </p>
              </div>

              <button
                type="button"
                onClick={closeCreateModal}
                disabled={submitting || uploading}
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 999,
                  border: "1px solid rgba(0,0,0,0.10)",
                  background: "rgba(255,255,255,0.74)",
                  cursor: submitting || uploading ? "not-allowed" : "pointer",
                  fontSize: 20,
                  lineHeight: 1,
                }}
              >
                ×
              </button>
            </div>

            <form
              className="merchant-form"
              onSubmit={handleSubmit}
              style={{ display: "grid", gap: 12 }}
            >
              <input
                className="merchant-input"
                value={form.title}
                onChange={(e) => updateField("title", e.target.value)}
                placeholder="Title, e.g. Tonal Layers"
              />

              <input
                className="merchant-input"
                value={form.subtitle}
                onChange={(e) => updateField("subtitle", e.target.value)}
                placeholder="Subtitle, e.g. Monochrome dressing for easy styling."
              />

              <textarea
                className="merchant-input"
                rows={4}
                value={form.notes}
                onChange={(e) => updateField("notes", e.target.value)}
                placeholder={
                  "Keywords / notes, one per line\nmonochrome vibe\nquiet luxury neutrals\ntexture does the talking"
                }
              />

              <div
                style={{
                  border: "1px dashed rgba(91,31,43,0.22)",
                  borderRadius: 16,
                  padding: 14,
                  background: "rgba(251,247,242,0.46)",
                }}
              >
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={(e) => handleUpload(e.target.files)}
                />

                {uploading ? (
                  <p style={{ margin: "10px 0 0", opacity: 0.72 }}>
                    Uploading images...
                  </p>
                ) : null}
              </div>

              <textarea
                className="merchant-input"
                rows={4}
                value={form.images}
                onChange={(e) => updateField("images", e.target.value)}
                placeholder={"Images, one per line\ntonallayers1.png\ntonallayers2.png\nor full URL"}
              />

              {parseLines(form.images).length ? (
                <div
                  style={{
                    display: "flex",
                    gap: 8,
                    flexWrap: "wrap",
                  }}
                >
                  {parseLines(form.images)
                    .slice(0, 12)
                    .map((imageUrl, index) => (
                      <div
                        key={`${imageUrl}_${index}`}
                        style={{
                          width: 54,
                          height: 68,
                          borderRadius: 12,
                          overflow: "hidden",
                          border: "1px solid rgba(176,140,108,0.26)",
                          background: "rgba(255,255,255,0.65)",
                        }}
                      >
                        <img
                          src={resolveImageUrl(imageUrl)}
                          alt={`Preview ${index + 1}`}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            display: "block",
                          }}
                        />
                      </div>
                    ))}
                </div>
              ) : null}

              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "4px 0",
                }}
              >
                <input
                  type="checkbox"
                  checked={form.is_live}
                  onChange={(e) => updateField("is_live", e.target.checked)}
                />
                <span className="merchant-label">Publish live</span>
              </label>

              {error ? <p className="merchant-error">{error}</p> : null}

              <div
                className="merchant-actions"
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 10,
                  paddingTop: 6,
                }}
              >
                <button
                  type="button"
                  onClick={closeCreateModal}
                  disabled={submitting || uploading}
                  style={{
                    padding: "10px 16px",
                    borderRadius: 14,
                    border: "1px solid rgba(0,0,0,0.10)",
                    background: "rgba(255,255,255,0.72)",
                    cursor: submitting || uploading ? "not-allowed" : "pointer",
                    fontWeight: 700,
                  }}
                >
                  Cancel
                </button>

                <button
                  className="gold-btn"
                  type="submit"
                  disabled={submitting || uploading}
                  style={{
                    minHeight: 44,
                    padding: "10px 18px",
                  }}
                >
                  {submitting ? "Creating..." : "Create edit"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}